import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomTextField from "../../../../../components/common/Textfield";
import useDebounce from "../../../../../hooks/useDebounce";
import apiService from "../../../../../service/Api";
import { RootState } from "../../../../../store/Index";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
}

interface MemoryBookAssignment {
  id: number;
  assigned_to: User;
  physical_comments: number | null;
}

interface AssignMemoryBookModalProps {
  open: boolean;
  onClose: () => void;
  deceasedName: string;
  streamId: number;
  onAssignSuccess?: () => void;
}

export const AssignMemoryBookModal: React.FC<AssignMemoryBookModalProps> = ({
  open,
  onClose,
  deceasedName,
  streamId,
  onAssignSuccess,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [userVal, setUserVal] = useState("");
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [assignmentId, setAssignmentId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [fetchingAssignment, setFetchingAssignment] = useState(false);
  const [collectedComments, setCollectedComments] = useState("");

  const debouncedUserSearch = useDebounce(userVal, 400);

  const closeAndReset = () => {
    setUserVal("");
    setUserOptions([]);
    setSelectedUser(null);
    setAssignmentId(null);
    setCollectedComments("");
    onClose();
  };

  // Fetch existing assignment when dialog opens
  const fetchExistingAssignment = useCallback(async () => {
    setFetchingAssignment(true);
    try {
      const response = await apiService.get<{
        results: MemoryBookAssignment[];
      }>(`/api/streams/memory-book-assignment/?stream=${streamId}`);
      if (response.data.results && response.data.results.length > 0) {
        const assignment = response.data.results[0];
        setAssignmentId(assignment.id);
        setSelectedUser(assignment.assigned_to);
        setCollectedComments(String(assignment.physical_comments || ""));
      }
    } catch (err) {
      console.error("Failed to fetch existing assignment:", err);
    } finally {
      setFetchingAssignment(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (open) fetchExistingAssignment();
  }, [open, fetchExistingAssignment]);

  // Fetch users on search
  const fetchUsers = useCallback(async (search: string) => {
    if (!search) {
      setUserOptions([]);
      return;
    }
    setUsersLoading(true);
    try {
      const users = await apiService.get<{
        count: number;
        results: User[];
        next: string | null;
        previous: string | null;
      }>("/api/authentication/users/", { search });
      setUserOptions(users.data.results || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(debouncedUserSearch);
  }, [debouncedUserSearch, fetchUsers]);

  // Assign or reassign
  const handleAssign = async () => {
    if (!selectedUser) return;

    setAssigning(true);
    try {
      const payload = {
        stream_id: streamId,
        assigned_to_id: selectedUser.id,
        physical_comments: parseInt(collectedComments, 10),
      };

      if (assignmentId) {
        await apiService.patch(
          `/api/streams/memory-book-assignment/${assignmentId}/`,
          payload,
        );
        toast.success(
          `Memory book reassigned to ${selectedUser.first_name} ${selectedUser.last_name}!`,
        );
      } else {
        const response = await apiService.post<MemoryBookAssignment>(
          `/api/streams/memory-book-assignment/`,
          payload,
        );
        setAssignmentId(response.data.id);
        toast.success(
          `Memory book assigned to ${selectedUser.first_name} ${selectedUser.last_name}!`,
        );
      }

      onAssignSuccess?.();
      closeAndReset();
    } catch {
      toast.error("Failed to assign memory book. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeAndReset} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Memory Book
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box mt={1}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: "text.secondary" }}
          >
            Assigning memory book for: <strong>{deceasedName}</strong>
          </Typography>

          {fetchingAssignment ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Box flex={3}>
                  <Autocomplete
                    multiple={false}
                    options={userOptions}
                    getOptionLabel={(option) =>
                      option?.id
                        ? `${option.id} - ${option.first_name} ${option.last_name}`
                        : "No Users To Display"
                    }
                    value={selectedUser}
                    onChange={(_, newValue) => setSelectedUser(newValue)}
                    inputValue={userVal}
                    onInputChange={(_, newInput) => setUserVal(newInput)}
                    loading={usersLoading}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        placeholder="Search users..."
                        variant="outlined"
                        helperText="Type 3 letters to search"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const label = option?.id
                          ? `${option.id} - ${option.first_name} ${option.last_name}`
                          : "No Users To Display";
                        return (
                          <Chip
                            label={label}
                            {...getTagProps({ index })}
                            key={option.id}
                            sx={{
                              backgroundColor: reduxTheme.primary_colors[800],
                              borderRadius: 1,
                              color: reduxTheme.primary_colors[100],
                            }}
                          />
                        );
                      })
                    }
                  />
                </Box>

                <Box flex={1} minWidth={160}>
                  <CustomTextField
                    fullWidth
                    type="number"
                    label="Collected Comments"
                    placeholder="0"
                    value={collectedComments}
                    onChange={(e) => setCollectedComments(e.target.value)}
                    helperText="Physical comments"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Box>
              </Box>

              <List
                sx={{
                  backgroundColor: reduxTheme.primary_colors[800],
                  borderRadius: 3,
                  mt: 1,
                  maxHeight: 300,
                  overflowY: "auto",
                  minHeight: 100,
                }}
              >
                {userVal.trim().length < 3 ? (
                  <ListItem>
                    <ListItemText
                      primary="Type at least 3 characters to search..."
                      sx={{ color: reduxTheme.primary_colors[100] }}
                    />
                  </ListItem>
                ) : usersLoading ? (
                  <ListItem>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 2, color: reduxTheme.primary_colors[100] }}
                    />
                    <ListItemText
                      primary="Searching..."
                      sx={{ color: reduxTheme.primary_colors[100] }}
                    />
                  </ListItem>
                ) : !selectedUser ? (
                  <ListItem>
                    <ListItemText
                      primary="No user selected."
                      sx={{ color: reduxTheme.primary_colors[100] }}
                    />
                  </ListItem>
                ) : (
                  <ListItem component="div">
                    <ListItemAvatar>
                      <Avatar
                        src={
                          selectedUser?.profile_picture_url ||
                          "/placeholder.svg"
                        }
                      />
                    </ListItemAvatar>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: reduxTheme.primary_colors[100] }}
                      >
                        {selectedUser.first_name} {selectedUser.last_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: reduxTheme.primary_colors[300] }}
                      >
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </ListItem>
                )}
              </List>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!selectedUser || assigning || !collectedComments}
          onClick={handleAssign}
          sx={{
            color: reduxTheme.primary_colors[900],
            backgroundColor: reduxTheme.primary_colors[400],
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {assignmentId ? "Reassign Book" : "Assign Book"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
